import { type AnimationProps, motion } from "framer-motion";
import { type CSSProperties, memo, type ReactElement, type ReactNode } from "react";
import { cn } from "src/utilities/shadcnUtils";

export interface TextProps extends Omit<AnimationProps, "children" | "style" | "paragraph"> {
  children: string | ReactElement<any> | Array<string | ReactElement<any>> | ReactNode;
  className?: string;
  style?: CSSProperties;
  paragraph?: boolean;
  value?: string;
  inline?: boolean;
}

const STYLES = {
  base: "font-['Hanken_Grotesk',sans-serif] tracking-[0.02em] text-[inherit] m-0",
  paragraph: "leading-[160%] inline",
  inline: "inline-block",
  header: "font-serif tracking-normal text-[inherit] m-0 text-4xl",
  thin: "font-thin",
  extraLight: "font-extralight",
  light: "font-light",
  regular: "font-normal",
  medium: "font-medium",
  semiBold: "font-semibold",
  bold: "font-bold",
  extraBold: "font-extrabold",
  black: "font-black",
};

export const Text = memo(({ children, className, style, paragraph, animate, inline, ...props }: TextProps) =>
  animate ? (
    <motion.p
      animate={animate}
      {...props}
      style={style}
      className={cn(STYLES.base, paragraph && STYLES.paragraph, inline && STYLES.inline, className)}
    >
      {children}
    </motion.p>
  ) : typeof children === "string" ? (
    <p
      style={style}
      className={cn(STYLES.base, paragraph && STYLES.paragraph, inline && STYLES.inline, className)}
    >
      {children}
    </p>
  ) : (
    <div
      style={style}
      className={cn(STYLES.base, paragraph && STYLES.paragraph, inline && STYLES.inline, className)}
    >
      {children}
    </div>
  )
);

export const BoldBase = memo(
  ({ children, className, style, paragraph, animate, value, inline, ...props }: TextProps) =>
    animate ? (
      <motion.b
        // @ts-expect-error
        value={value}
        animate={animate}
        {...props}
        style={style}
        className={cn(STYLES.base, paragraph && STYLES.paragraph, inline && STYLES.inline, className)}
      >
        {children}
      </motion.b>
    ) : (
      <b
        // @ts-expect-error
        value={value}
        style={style}
        className={cn(STYLES.base, paragraph && STYLES.paragraph, inline && STYLES.inline, className)}
      >
        {children}
      </b>
    )
);

export const LightText = memo(({ children, className, ...props }: TextProps) => (
  <Text {...props} className={cn(STYLES.light, className)}>
    {children}
  </Text>
));
export const RegularText = memo(({ children, className, ...props }: TextProps) => (
  <Text {...props} className={cn(STYLES.regular, className)}>
    {children}
  </Text>
));
export const MediumText = memo(({ children, className, ...props }: TextProps) => (
  <Text {...props} className={cn(STYLES.medium, className)}>
    {children}
  </Text>
));
export const SemiBoldText = memo(({ children, className, ...props }: TextProps) => (
  <BoldBase {...props} className={cn(STYLES.semiBold, className)}>
    {children}
  </BoldBase>
));
export const BoldText = memo(({ children, className, ...props }: TextProps) => (
  <BoldBase {...props} className={cn(STYLES.bold, className)}>
    {children}
  </BoldBase>
));

export const HeaderText = memo(({ children, className }: TextProps) => (
  <p className={cn(STYLES.header, className)}>{children}</p>
));
